import {useQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {AgentInfo} from '../types/session'
import {isFeatureEnabled} from '@github-ui/feature-flags'

const DEFAULT_COPILOT_AGENT_INFO: AgentInfo = {
  slug: 'copilot-swe-agent',
  display_name: 'Copilot',
  avatar_url: '',
  doc_url: 'https://gh.io/copilot-coding-agent-docs',
}

export function generateAgentInfoQueryKey(agentId: number | undefined | null, use3pAgentInfo: boolean) {
  return ['agent-info', agentId, use3pAgentInfo]
}

/**
 *
 * This hook fetches agent metadata (name, avatar, etc.) from the monolith
 * for agents that are returned from CAPI sessions but don't include
 * full agent information.
 *
 * When the feature flag is disabled, it returns default Copilot agent data.
 *
 */
export function useAgentInfo(agentId: number | undefined | null) {
  const use3pAgentInfo = isFeatureEnabled('copilot_3p_agent_info')

  return useQuery<AgentInfo>({
    queryKey: generateAgentInfoQueryKey(agentId, use3pAgentInfo),
    queryFn: async () => {
      if (!use3pAgentInfo) {
        return DEFAULT_COPILOT_AGENT_INFO
      }

      const response = await reactFetchJSON(`/copilot/agent-sessions/agents/${agentId}`)

      if (!response.ok) {
        throw new Error(`Failed to fetch agent info for agent ${agentId}`)
      }

      const data = await response.json()
      return data.agent_info
    },
    enabled: !!agentId,
    staleTime: Infinity,
  })
}
